import { useTranslation } from '../../../i18n';
import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Space, Modal, Form, Input, message, Popconfirm, Switch, Tag, Alert } from 'antd';
import { PlusOutlined, DeleteOutlined, LinkOutlined } from '@ant-design/icons';
import hostSharedService from '../../../../shared/services/hostSharedService';

const HotlinkManager = ({ vhostName }) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [enabled, setEnabled] = useState(false);
    const [dataSource, setDataSource] = useState([]);
    const [modalVisible, setModalVisible] = useState(false);
    const [form] = Form.useForm();

    useEffect(() => {
        if (vhostName) {
            loadData();
        }
    }, [vhostName]);

    const loadData = async () => {
        setLoading(true);
        try {
            const response = await hostSharedService.getHotlink(vhostName);
            if (response.success && response.data) {
                setEnabled(response.data.enabled || false);
                const items = response.data.items || response.data.rules || [];
                setDataSource(items.map((item, index) => ({
                    ...item,
                    key: item.id || index
                })));
            } else {
                message.error(response.error || t('Failed to load hotlink protection settings'));
                setDataSource([]);
            }
        } catch (error) {
            message.error(t('Failed to load: {{message}}', { message: error.message }));
            setDataSource([]);
        } finally {
            setLoading(false);
        }
    };

    const handleToggle = async (checked) => {
        setLoading(true);
        try {
            const response = await hostSharedService.toggleHotlink(vhostName, checked ? 1 : 0);
            if (response.success) {
                message.success(checked ? t('Hotlink protection enabled') : t('Hotlink protection disabled'));
                setEnabled(checked);
                loadData();
            } else {
                message.error(response.error || t('Failed to change status'));
                setDataSource([]);
            }
        } catch (error) {
            message.error(t('Failed to change status: {{message}}', { message: error.message }));
            setDataSource([]);
        } finally {
            setLoading(false);
        }
    };

    const handleAdd = () => {
        form.resetFields();
        setModalVisible(true);
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);

            const response = await hostSharedService.addHotlink(
                vhostName,
                values.path,
                values.referer || '',
                values.redirect || ''
            );
            if (response.success) {
                message.success(t('Added successfully'));
                setModalVisible(false);
                loadData();
            } else {
                message.error(response.error || t('Failed to add'));
                setDataSource([]);
            }
        } catch (error) {
            if (error.errorFields) {
                return;
            }
            message.error(t('Failed to add: {{message}}', { message: error.message }));
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        setLoading(true);
        try {
            const response = await hostSharedService.deleteHotlink(vhostName, id);
            if (response.success) {
                message.success(t('Deleted successfully'));
                loadData();
            } else {
                message.error(response.error || t('Deletion failed'));
                setDataSource([]);
            }
        } catch (error) {
            message.error(t('Deletion failed: {{message}}', { message: error.message }));
            setDataSource([]);
        } finally {
            setLoading(false);
        }
    };

    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 80,
        },
        {
            title: t('Protected path (regex)'),
            dataIndex: 'path',
            key: 'path',
            render: (text) => <span style={{ fontFamily: 'monospace' }}>{text}</span>,
        },
        {
            title: t('Allowed referrers (regex)'),
            dataIndex: 'referer',
            key: 'referer',
            render: (text) =>
                text ? <span style={{ fontFamily: 'monospace' }}>{text}</span> : <Tag color="blue">{t('Same origin')}</Tag>,
        },
        {
            title: t('Invalid request redirect'),
            dataIndex: 'redirect',
            key: 'redirect',
            render: (text) => (text ? <span style={{ fontFamily: 'monospace' }}>{text}</span> : <Tag>{t('Deny')}</Tag>),
        },
        {
            title: t('Actions'),
            key: 'action',
            width: 120,
            render: (_, record) => (
                <Space>
                    <Popconfirm
                        title={t('Delete this rule?')}
                        onConfirm={() => handleDelete(record.id)}
                        okText={t('OK')}
                        cancelText={t('Cancel')}
                    >
                        <Button type="link" danger size="small" icon={<DeleteOutlined />}>
                            {t('Delete')}
                        </Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <Card
            title={
                <Space>
                    <LinkOutlined />
                    {t('Manage hotlink protection')}
                </Space>
            }
            extra={
                <Space>
                    <Switch
                        checkedChildren={t('Enable')}
                        unCheckedChildren={t('Disable')}
                        checked={enabled}
                        onChange={handleToggle}
                        loading={loading}
                    />
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={handleAdd}
                        disabled={!enabled}
                    >
                        {t('Add rule')}
                    </Button>
                </Space>
            }
        >
            <Alert
                message={t('About hotlink protection')}
                description={t('After you set a protected path, only requests matching the allowed referrer regex can access the resource. Unmatched requests can be denied or redirected to a specified URL. Regular expressions and wildcards are supported.')}
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
            />

            <Table
                columns={columns}
                dataSource={dataSource}
                loading={loading}
                pagination={{ pageSize: 10 }}
            />

            <Modal
                title={t('Add hotlink protection rule')}
                open={modalVisible}
                onOk={handleSubmit}
                onCancel={() => setModalVisible(false)}
                confirmLoading={loading}
            >
                <Form form={form} layout="vertical">
                    <Form.Item
                        label={t('Protected path (regex)')}
                        name="path"
                        rules={[{ required: true, message: t('Please enter a protected path') }]}
                        extra={t('Example: ^/download or ^/(images|video)/')}
                    >
                        <Input placeholder="^/download" />
                    </Form.Item>

                    <Form.Item
                        label={t('Allowed referrers (regex, optional)')}
                        name="referer"
                        extra={t('Leave blank to allow same-origin access only, e.g. ^https?://(www\\\\.)?example\\\\.com')}
                    >
                        <Input placeholder={t('E.g. ^https?://(www\\\\.)?example\\\\.com')} />
                    </Form.Item>

                    <Form.Item
                        label={t('Invalid request redirect (optional)')}
                        name="redirect"
                        extra={t('Leave blank to deny directly, or enter /403.html or https://example.com/deny')}
                    >
                        <Input placeholder={t('E.g. /403.html')} />
                    </Form.Item>
                </Form>
            </Modal>
        </Card>
    );
};

export default HotlinkManager;
